(() => {
  const REVEAL_CLASS = 'reveal';
  const VISIBLE_CLASS = 'is-visible';

  /**
   * ✅ KEY IDEAS TO PREVENT FLICKER / DISAPPEAR / GLITCH:
   * 1) Only animate “containers” (sections/cards/tiles), not inner text elements.
   *    (Animating .rte/.text-block causes re-render flicker, especially for prices.)
   * 2) Never touch dynamic UI + price markup.
   * 3) When Shopify re-renders DOM (sorting, theme editor, variant changes),
   *    we tag *new* containers only and reveal anything already in/near view immediately.
   */

  // ✅ What should animate in (containers only)
  const TARGET_SELECTOR = [
    '.shopify-section',
    '.section',
    '.product-grid__item',
    '.resource-list__item',
    '.product-card',
    '.collection-card',
    '.resource-card',
    '.featured-collection-block',
    '.image-block',
    '.video-placeholder-wrapper'
  ].join(',');

  // ✅ Things we should NOT mess with (dynamic UI, overlays, and price-related elements)
  const EXCLUDE_SELECTOR = [
    'header', '.header', 'header-component', '.header-drawer',
    'dialog', '.drawer', '.predictive-search', '[scroll-lock]',

    // Footer (robust)
    'footer', '.footer',
    '[id^="shopify-section-"][id*="footer"]',
    '[class*="footer"]',

    // Pricing
    'product-price', '.price', '.compare-at-price', '.unit-price', '.tax-note',

    // Facets
    'sorting-filter-component', '.facets', '.facets__form', '.facets__panel',
    '.facets__summary', '.sorting-filter__options'
  ].join(',');


  // Avoid repeatedly scanning too often when DOM is very active
  let observer;
  let scheduled = false;

  const prefersReduced = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const inOrNearViewport = (el) => {
    const rect = el.getBoundingClientRect();
    // Reveal if any part is within a comfortable pre-load range
    return rect.top < window.innerHeight * 0.95 && rect.bottom > -50;
  };

  const ensureObserver = () => {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add(VISIBLE_CLASS);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    return observer;
  };

  const tagAndObserve = (root = document) => {
    const targets = root.querySelectorAll(TARGET_SELECTOR);

    // Accessibility: prefer-reduced-motion => show everything immediately, no IO
    if (prefersReduced()) {
      targets.forEach((el) => {
        if (el.closest(EXCLUDE_SELECTOR)) return;
        el.classList.add(REVEAL_CLASS);
        el.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    // Old browser fallback
    if (!('IntersectionObserver' in window)) {
      targets.forEach((el) => {
        if (el.closest(EXCLUDE_SELECTOR)) return;
        el.classList.add(REVEAL_CLASS);
        el.classList.add(VISIBLE_CLASS);
      });
      return;
    }

    const obs = ensureObserver();

    targets.forEach((el) => {
      if (el.closest(EXCLUDE_SELECTOR)) return;

      // Tag once
      if (!el.classList.contains(REVEAL_CLASS)) {
        el.classList.add(REVEAL_CLASS);
      }

      // ✅ Important: if already in/near view, show immediately (prevents “blank” + flicker)
      if (inOrNearViewport(el)) {
        el.classList.add(VISIBLE_CLASS);
        obs.unobserve(el);
        return;
      }

      // Otherwise observe until it enters viewport
      if (!el.classList.contains(VISIBLE_CLASS)) {
        obs.observe(el);
      }
    });
  };

  const initReveal = (root = document) => {
    tagAndObserve(root);
  };

  const scheduleInit = (root = document) => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      initReveal(root);
    });
  };

  // Initial load
  document.addEventListener('DOMContentLoaded', () => scheduleInit());

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (e) => scheduleInit(e.target));

  // Catch dynamic DOM changes (filters/sorting/search results/etc.)
  const mo = new MutationObserver((mutations) => {
    // Only react if nodes were added (typography changes + filtering often replace nodes)
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        scheduleInit();
        break;
      }
    }
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
